<template>
  <div class="markdown-overlay" @click.self="$emit('close')">
    <div class="markdown-modal">
      <div class="modal-header">
        <div class="title-left">
          <span class="title-icon">📄</span>
          <span class="title-text">优化 Markdown 报告</span>
        </div>
        <div class="header-actions">
          <button class="btn-export" @click="exportMarkdown">导出 Markdown</button>
          <button class="close-btn" @click="$emit('close')">×</button>
        </div>
      </div>
      <div class="modal-body">
        <div class="ctx-card" v-if="data && data.context">
          <div class="ctx-title">上下文摘要</div>
          <div class="ctx-grid">
            <div class="ctx-item"><b>目标：</b>{{ data.context.target }}</div>
            <div class="ctx-item"><b>本品对象：</b>{{ (data.context.selectedObjects || []).join('、') }}</div>
            <div class="ctx-item" v-if="data.context.competitors && data.context.competitors.selectedObjects && data.context.competitors.selectedObjects.length">
              <b>竞品对象：</b>{{ data.context.competitors.selectedObjects.join('、') }}
            </div>
            <div class="ctx-item" v-if="data.context.userRole"><b>用户角色：</b>{{ data.context.userRole }}</div>
            <div class="ctx-item" v-if="data.context.roleDescription"><b>一般诉求：</b>{{ data.context.roleDescription }}</div>
          </div>
        </div>
        <!-- 卡片数据展示（立即可见） -->
        <div class="cards-section" v-if="data && data.context && (data.context.selectedAspects || []).length">
          <div class="cards-title">口碑卡片数据（所选口碑点）</div>
          <div class="cards-grid">
            <div v-for="asp in data.context.selectedAspects" :key="asp" class="card-item">
              <div class="card-header">
                <span class="card-title">📌 {{ asp }}</span>
                <div class="card-metrics" v-if="data.context.aspectMetrics && data.context.aspectMetrics[asp]">
                  <span>覆盖 {{ (data.context.aspectMetrics[asp].coverage * 100).toFixed(1) }}%</span>
                  <span>负面 {{ (data.context.aspectMetrics[asp].negRate * 100).toFixed(1) }}%</span>
                </div>
              </div>
              <div class="card-content">
                <div class="card-line pos" v-if="data.context.aspectInsights && data.context.aspectInsights[asp] && data.context.aspectInsights[asp].examples && data.context.aspectInsights[asp].examples.positive && data.context.aspectInsights[asp].examples.positive.length">
                  正面：{{ formatExampleLine(data.context.aspectInsights[asp].examples.positive) }}
                </div>
                <div class="card-line empty" v-else>正面：暂无</div>
                <div class="card-line neg" v-if="data.context.aspectInsights && data.context.aspectInsights[asp] && data.context.aspectInsights[asp].examples && data.context.aspectInsights[asp].examples.negative && data.context.aspectInsights[asp].examples.negative.length">
                  负面：{{ formatExampleLine(data.context.aspectInsights[asp].examples.negative) }}
                </div>
                <div class="card-line empty" v-else>负面：暂无</div>
                <div class="card-line ai" v-if="data.context.aspectInsights && data.context.aspectInsights[asp] && data.context.aspectInsights[asp].aiLine">
                  AI分析：{{ data.context.aspectInsights[asp].aiLine }}
                </div>
                <div class="card-line ai" v-else>AI分析：生成中...</div>

                <!-- 竞品示例（若存在） -->
                <div class="comp-block" v-if="data.context.aspectInsights && data.context.aspectInsights.__competitor__ && data.context.aspectInsights.__competitor__[asp]">
                  <div class="comp-title">对比：竞品示例</div>
                  <div class="card-line pos" v-if="data.context.aspectInsights.__competitor__[asp].examples && data.context.aspectInsights.__competitor__[asp].examples.positive && data.context.aspectInsights.__competitor__[asp].examples.positive.length">
                    正面（竞品）：{{ formatExampleLine(data.context.aspectInsights.__competitor__[asp].examples.positive) }}
                  </div>
                  <div class="card-line empty" v-else>正面（竞品）：暂无</div>
                  <div class="card-line neg" v-if="data.context.aspectInsights.__competitor__[asp].examples && data.context.aspectInsights.__competitor__[asp].examples.negative && data.context.aspectInsights.__competitor__[asp].examples.negative.length">
                    负面（竞品）：{{ formatExampleLine(data.context.aspectInsights.__competitor__[asp].examples.negative) }}
                  </div>
                  <div class="card-line empty" v-else>负面（竞品）：暂无</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="markdown-view" v-html="renderedMarkdown"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
export default {
  name: 'OptimizationMarkdownOverlay',
  props: {
    visible: { type: Boolean, default: false },
    data: { type: Object, default: () => ({}) }
  },
  computed: {
    renderedMarkdown() {
      let content = (this.data && this.data.markdown) ? this.data.markdown : '暂无报告内容'
      // 处理AI可能输出的三引号包裹，移除最外层代码块以正常渲染表格
      content = this.stripMarkdownFences(content)
      content = this.normalizeTables(content)
      marked.setOptions({ gfm: true, breaks: true, headerIds: false, mangle: false })
      try {
        return marked.parse(String(content))
      } catch (e) {
        console.warn('Markdown 解析失败:', e)
        return `<pre>${String(content)}</pre>`
      }
    }
  },
  methods: {
    stripMarkdownFences(text = '') {
      // 移除所有三引号代码块的围栏，仅保留内部文本
      return String(text).replace(/```[a-zA-Z0-9_-]*\n([\s\S]*?)\n```/g, (m, inner) => inner)
    },
    normalizeTables(md = '') {
      const lines = String(md).split(/\r?\n/)
      const out = []
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const isTableHeader = /^\s*\|[^|]+\|.*$/.test(line)
        const next = lines[i + 1] || ''
        const isSep = /^\s*\|\s*[-:]+.*\|\s*$/.test(next)
        if (isTableHeader && isSep) {
          // 确保前一行为空，提升兼容性
          if (out.length && out[out.length - 1].trim() !== '') {
            out.push('')
          }
          // 规范化头/分隔/数据行的列数与结尾竖线
          const headerCells = line.trim().split('|').filter(c => c.trim() !== '')
          const colCount = headerCells.length
          const normalizedHeader = `| ${headerCells.map(c => c.trim()).join(' | ')} |`
          const normalizedSep = `| ${Array(colCount).fill('---').join(' | ')} |`
          out.push(normalizedHeader)
          out.push(normalizedSep)
          i += 1 // 跳过原分隔行
          // 处理数据行，直到遇到非表格行
          while (i + 1 < lines.length) {
            const peek = lines[i + 1]
            if (!/^\s*\|.*$/.test(peek)) break
            const dataCells = peek.trim().split('|').filter(c => c.trim() !== '')
            // 修复列数，过少用空格补齐，过多截断
            if (dataCells.length < colCount) {
              while (dataCells.length < colCount) dataCells.push('')
            } else if (dataCells.length > colCount) {
              dataCells.length = colCount
            }
            out.push(`| ${dataCells.map(c => c.trim()).join(' | ')} |`)
            i += 1
          }
          continue
        }
        out.push(line)
      }
      return out.join('\n')
    },
    formatExampleLine(items = [], limit = 2) {
      const texts = (items || []).slice(0, limit).map(i => (i && i.text) ? String(i.text).trim() : '').filter(Boolean)
      if (!texts.length) return '暂无'
      return texts.join(' ｜ ')
    },
    exportMarkdown() {
      const content = (this.data && this.data.markdown) ? this.data.markdown : ''
      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `优化报告_${Date.now()}.md`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.markdown-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; z-index: 10002; }
.markdown-modal { width: 92vw; max-width: 1100px; max-height: 88vh; background: #fff; border-radius: 16px; box-shadow: 0 18px 56px rgba(0,0,0,0.25); display: flex; flex-direction: column; overflow: hidden; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; background: linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%); color: #fff; }
.title-left { display: flex; align-items: center; gap: 10px; font-weight: 700; }
.header-actions { display: flex; align-items: center; gap: 10px; }
.btn-export { background: rgba(255,255,255,0.2); color: #fff; border: none; border-radius: 8px; padding: 8px 12px; cursor: pointer; }
.close-btn { width: 28px; height: 28px; border: none; background: rgba(255,255,255,0.2); color: #fff; border-radius: 50%; cursor: pointer; }
.modal-body { padding: 12px 16px; overflow-y: auto; }
.ctx-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px 12px; margin-bottom: 12px; }
.ctx-title { font-weight: 700; color: #111827; margin-bottom: 8px; }
.ctx-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 8px; }
.ctx-item { color: #374151; }
.cards-section { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px 12px; margin-bottom: 12px; }
.cards-title { font-weight: 700; color: #111827; margin-bottom: 8px; }
.cards-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.card-item { background: #fafafa; border: 1px solid #eee; border-radius: 12px; padding: 10px 12px; }
.card-header { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 6px; }
.card-title { font-weight: 600; color: #1f2937; }
.card-metrics { display: flex; gap: 8px; color: #6b7280; font-size: 12px; }
.card-content { display: flex; flex-direction: column; gap: 6px; }
.card-line { font-size: 12px; line-height: 1.6; border-radius: 8px; padding: 8px 10px; }
.card-line.pos { color: #064e3b; background: #ecfdf5; border: 1px solid #10b981; }
.card-line.neg { color: #7f1d1d; background: #fef2f2; border: 1px solid #ef4444; }
.card-line.ai { color: #1e3a8a; background: #eff6ff; border: 1px solid #60a5fa; }
.card-line.empty { color: #64748b; background: #f8fafc; border: 1px dashed #cbd5e1; }
.comp-block { margin-top: 8px; border-top: 1px dashed #e5e7eb; padding-top: 6px; }
.comp-title { font-weight: 600; font-size: 12px; color: #4b5563; margin-bottom: 6px; }
.markdown-view { background: #ffffff; color: #111827; border-radius: 12px; padding: 20px; min-height: 360px; border: 1px solid #e5e7eb; margin: 12px 12px 16px; }
.markdown-view h1, .markdown-view h2, .markdown-view h3 { color: #0f172a; margin: 0.6em 0 0.4em; }
.markdown-view p { line-height: 1.8; margin: 0.5em 0; }
.markdown-view ::v-deep ul { list-style-type: disc; list-style-position: outside; margin: 0.6em 0 0.6em 1.4em; padding: 0; }
.markdown-view ::v-deep ol { list-style-type: decimal; list-style-position: outside; margin: 0.6em 0 0.6em 1.4em; padding: 0; }
.markdown-view ::v-deep li { margin: 0.25em 0; line-height: 1.8; }
.markdown-view ::v-deep ul li::marker { color: #3b82f6; font-weight: 600; }
.markdown-view ::v-deep ol li::marker { color: #16a34a; font-weight: 600; }
.markdown-view ::v-deep li ul, .markdown-view ::v-deep li ol { margin-left: 1.2em; }
.markdown-view code { background: #f1f5f9; color: #0f172a; padding: 2px 6px; border-radius: 6px; }
.markdown-view pre { background: #f8fafc; color: #0f172a; padding: 10px 12px; border-radius: 8px; overflow: auto; border: 1px solid #e5e7eb; }
/* 使用深度选择器确保 v-html 内容在 scoped 下也能被样式覆盖 */
.markdown-view ::v-deep table { width: 100%; border-collapse: collapse; margin: 10px 0; background: #ffffff; }
.markdown-view ::v-deep th, .markdown-view ::v-deep td { border: 1px solid #e5e7eb; padding: 8px 10px; text-align: left; }
.markdown-view ::v-deep thead th { background: #f3f4f6; color: #111827; }
.markdown-view ::v-deep tbody tr:nth-child(odd) { background: #fafafa; }
.markdown-view blockquote { border-left: 4px solid #60a5fa; padding-left: 12px; color: #334155; margin: 8px 0; background: #f8fafc; }
</style>